import React, { useState, useRef } from 'react';

const RichTextToolbar = ({ value, onChange, placeholder }) => {
  const textareaRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  // Formatting options
  const formattingOptions = [
    {
      name: 'bold',
      icon: 'B',
      tooltip: 'Bold (Ctrl+B)',
      tag: 'strong',
      shortcut: 'Ctrl+B'
    },
    {
      name: 'italic',
      icon: 'I',
      tooltip: 'Italic (Ctrl+I)',
      tag: 'em',
      shortcut: 'Ctrl+I'
    },
    {
      name: 'underline',
      icon: 'U',
      tooltip: 'Underline (Ctrl+U)',
      tag: 'u',
      shortcut: 'Ctrl+U'
    },
    {
      name: 'strikethrough',
      icon: 'S',
      tooltip: 'Strikethrough',
      tag: 's'
    }
  ];

  const headingOptions = [
    { name: 'h1', label: 'H1', tooltip: 'Heading 1' },
    { name: 'h2', label: 'H2', tooltip: 'Heading 2' },
    { name: 'h3', label: 'H3', tooltip: 'Heading 3' },
    { name: 'h4', label: 'H4', tooltip: 'Heading 4' }
  ];

  const listOptions = [
    {
      name: 'ul',
      icon: '•',
      tooltip: 'Bullet List',
      tag: 'ul'
    },
    {
      name: 'ol',
      icon: '1.',
      tooltip: 'Numbered List',
      tag: 'ol'
    }
  ];

  const colorOptions = [
    { name: 'default', color: '#374151', label: 'Default' },
    { name: 'red', color: '#dc2626', label: 'Red' },
    { name: 'orange', color: '#ea580c', label: 'Orange' },
    { name: 'amber', color: '#d97706', label: 'Amber' },
    { name: 'green', color: '#16a34a', label: 'Green' },
    { name: 'blue', color: '#2563eb', label: 'Blue' },
    { name: 'purple', color: '#7c3aed', label: 'Purple' },
    { name: 'pink', color: '#ec4899', label: 'Pink' }
  ];

  // Get current selection
  const getSelection = () => {
    const textarea = textareaRef.current;
    if (!textarea) return { start: 0, end: 0 };

    return {
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
      text: textarea.value.substring(
        textarea.selectionStart,
        textarea.selectionEnd
      )
    };
  };

  // Set selection
  const setTextSelection = (start, end) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Prevent page scroll by maintaining scroll position
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    textarea.focus();
    textarea.setSelectionRange(start, end);

    // Restore scroll position
    window.scrollTo(0, scrollTop);
  };

  // Apply formatting to selected text
  const applyFormatting = (tag, attributes = {}) => {
    const currentSelection = getSelection();
    const { start, end, text } = currentSelection;

    if (start === end) {
      // No selection, insert at cursor
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const newText =
        beforeText +
        `<${tag}>${attributes.placeholder || 'text'}</${tag}>` +
        afterText;
      onChange(newText);

      // Set cursor position after inserted tag
      const newPosition =
        start + `<${tag}>`.length + (attributes.placeholder || 'text').length;
      setTimeout(() => setTextSelection(newPosition, newPosition), 0);
    } else {
      // Apply to selected text
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const formattedText = `<${tag}>${text}</${tag}>`;
      const newText = beforeText + formattedText + afterText;
      onChange(newText);

      // Restore selection
      setTimeout(
        () => setTextSelection(start, start + formattedText.length),
        0
      );
    }
  };

  // Apply color to selected text
  const applyColor = (color) => {
    // Preserve scroll position before applying color
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    const currentSelection = getSelection();
    const { start, end, text } = currentSelection;

    if (start === end) {
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const newText =
        beforeText +
        `<span style="color: ${color}">colored text</span>` +
        afterText;
      onChange(newText);

      const newPosition =
        start + `<span style="color: ${color}">`.length + 'colored text'.length;
      setTimeout(() => {
        setTextSelection(newPosition, newPosition);
        // Restore scroll position after color application
        window.scrollTo(0, scrollTop);
      }, 0);
    } else {
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const formattedText = `<span style="color: ${color}">${text}</span>`;
      const newText = beforeText + formattedText + afterText;
      onChange(newText);

      setTimeout(() => {
        setTextSelection(start, start + formattedText.length);
        // Restore scroll position after color application
        window.scrollTo(0, scrollTop);
      }, 0);
    }
    setShowColorPicker(false);
  };

  // Insert link
  const insertLink = () => {
    const currentSelection = getSelection();
    const { start, end, text } = currentSelection;

    if (linkUrl.trim()) {
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const finalLinkText = linkText.trim() || text || 'link';
      const formattedText = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${finalLinkText}</a>`;
      const newText = beforeText + formattedText + afterText;
      onChange(newText);

      setTimeout(
        () => setTextSelection(start, start + formattedText.length),
        0
      );
    }

    setShowLinkDialog(false);
    setLinkUrl('');
    setLinkText('');
  };

  // Insert list
  const insertList = (tag) => {
    const currentSelection = getSelection();
    const { start, end, text } = currentSelection;

    if (start === end) {
      // Insert empty list
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const listItem =
        tag === 'ul' ? '<li>List item</li>' : '<li>List item</li>';
      const newText = beforeText + `<${tag}>${listItem}</${tag}>` + afterText;
      onChange(newText);

      const newPosition = start + `<${tag}><li>`.length + 'List item'.length;
      setTimeout(() => setTextSelection(newPosition, newPosition), 0);
    } else {
      // Convert selected text to list
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const lines = text.split('\n').filter((line) => line.trim());
      const listItems = lines.map((line) => `<li>${line.trim()}</li>`).join('');
      const formattedText = `<${tag}>${listItems}</${tag}>`;
      const newText = beforeText + formattedText + afterText;
      onChange(newText);

      setTimeout(
        () => setTextSelection(start, start + formattedText.length),
        0
      );
    }
  };

  // Insert heading
  const insertHeading = (level) => {
    const currentSelection = getSelection();
    const { start, end, text } = currentSelection;

    if (start === end) {
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const newText = beforeText + `<${level}>Heading</${level}>` + afterText;
      onChange(newText);

      const newPosition = start + `<${level}>`.length + 'Heading'.length;
      setTimeout(() => setTextSelection(newPosition, newPosition), 0);
    } else {
      const beforeText = value.substring(0, start);
      const afterText = value.substring(end);
      const formattedText = `<${level}>${text}</${level}>`;
      const newText = beforeText + formattedText + afterText;
      onChange(newText);

      setTimeout(
        () => setTextSelection(start, start + formattedText.length),
        0
      );
    }
  };

  // Insert special elements
  const insertElement = (element) => {
    const currentSelection = getSelection();
    const { start, end } = currentSelection;

    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    let insertText = '';
    switch (element) {
      case 'hr':
        insertText = '<hr />';
        break;
      case 'quote':
        insertText = '<blockquote>Quote text here</blockquote>';
        break;
      case 'code':
        insertText = '<code>code snippet</code>';
        break;
      case 'table':
        insertText = `<table class="border border-gray-300 border-collapse">
   <thead>
     <tr>
       <th class="px-4 py-2 border border-gray-300">Header 1</th>
       <th class="px-4 py-2 border border-gray-300">Header 2</th>
     </tr>
   </thead>
   <tbody>
     <tr>
       <td class="px-4 py-2 border border-gray-300">Cell 1</td>
       <td class="px-4 py-2 border border-gray-300">Cell 2</td>
     </tr>
   </tbody>
 </table>`;
        break;
      default:
        // Handle unknown element types gracefully
        console.warn(`Unknown element type: ${element}`);
        return;
    }

    const newText = beforeText + insertText + afterText;
    onChange(newText);

    const newPosition = start + insertText.length;
    setTimeout(() => setTextSelection(newPosition, newPosition), 0);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          applyFormatting('strong');
          break;
        case 'i':
          e.preventDefault();
          applyFormatting('em');
          break;
        case 'u':
          e.preventDefault();
          applyFormatting('u');
          break;
        case 'k':
          e.preventDefault();
          setShowLinkDialog(true);
          break;
        default:
          // Handle other keyboard shortcuts if needed
          break;
      }
    }
  };

  // Update selection on textarea events
  const handleTextareaSelect = () => {
    setSelection(getSelection());
  };

  const handleTextareaFocus = () => {
    setIsFocused(true);
    setSelection(getSelection());
  };

  const handleTextareaBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-300">
      {/* Toolbar */}
      <div className="p-3 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Text Formatting */}
          <div className="flex gap-1 items-center pr-3 border-r border-gray-300">
            {formattingOptions.map((option) => (
              <button
                key={option.name}
                onClick={() => applyFormatting(option.tag)}
                className="p-2 text-sm font-bold text-gray-700 rounded transition-colors hover:bg-gray-200"
                title={option.tooltip}>
                {option.icon}
              </button>
            ))}
          </div>

          {/* Headings */}
          <div className="flex gap-1 items-center pr-3 border-r border-gray-300">
            {headingOptions.map((option) => (
              <button
                key={option.name}
                onClick={() => insertHeading(option.name)}
                className="p-2 text-sm font-semibold text-gray-700 rounded transition-colors hover:bg-gray-200"
                title={option.tooltip}>
                {option.label}
              </button>
            ))}
          </div>

          {/* Lists */}
          <div className="flex gap-1 items-center pr-3 border-r border-gray-300">
            {listOptions.map((option) => (
              <button
                key={option.name}
                onClick={() => insertList(option.tag)}
                className="p-2 text-sm text-gray-700 rounded transition-colors hover:bg-gray-200"
                title={option.tooltip}>
                {option.icon}
              </button>
            ))}
          </div>

          {/* Color Picker */}
          <div className="relative pr-3 border-r border-gray-300">
            <button
              onClick={() => {
                // Preserve scroll position when toggling color picker
                const scrollTop =
                  window.pageYOffset || document.documentElement.scrollTop;
                setShowColorPicker(!showColorPicker);
                // Restore scroll position after state change
                setTimeout(() => window.scrollTo(0, scrollTop), 0);
              }}
              className="flex gap-1 items-center p-2 text-sm text-gray-700 rounded transition-colors hover:bg-gray-200"
              title="Text Color">
              <span className="w-4 h-4 bg-gradient-to-r from-red-500 to-purple-500 rounded"></span>
              A
            </button>

            {showColorPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-10 min-w-[200px]">
                <div className="grid grid-cols-4 gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => applyColor(color.color)}
                      className="flex flex-col gap-1 items-center p-2 text-xs rounded transition-colors hover:bg-gray-100"
                      title={color.label}>
                      <div
                        className="w-6 h-6 rounded border border-gray-300"
                        style={{ backgroundColor: color.color }}></div>
                      <span className="text-xs">{color.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Link */}
          <div className="pr-3 border-r border-gray-300">
            <button
              onClick={() => setShowLinkDialog(true)}
              className="p-2 text-sm text-gray-700 rounded transition-colors hover:bg-gray-200"
              title="Insert Link (Ctrl+K)">
              🔗
            </button>
          </div>

          {/* Special Elements */}
          <div className="flex gap-1 items-center pr-3 border-r border-gray-300">
            <button
              onClick={() => insertElement('hr')}
              className="p-2 text-sm text-gray-700 rounded transition-colors hover:bg-gray-200"
              title="Horizontal Rule">
              —
            </button>
            <button
              onClick={() => insertElement('quote')}
              className="p-2 text-sm text-gray-700 rounded transition-colors hover:bg-gray-200"
              title="Blockquote">
              "
            </button>
            <button
              onClick={() => insertElement('code')}
              className="p-2 text-sm text-gray-700 rounded transition-colors hover:bg-gray-200"
              title="Code">
              &lt;/&gt;
            </button>
            <button
              onClick={() => insertElement('table')}
              className="p-2 text-sm text-gray-700 rounded transition-colors hover:bg-gray-200"
              title="Table">
              ⊞
            </button>
          </div>

          {/* Help */}
          <div className="text-xs text-gray-500">
            <span className="hidden sm:inline">Shortcuts: </span>
            <span className="hidden sm:inline">
              Ctrl+B, Ctrl+I, Ctrl+U, Ctrl+K
            </span>
          </div>
        </div>
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onSelect={handleTextareaSelect}
        onFocus={handleTextareaFocus}
        onBlur={handleTextareaBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={8}
        className="px-4 py-3 w-full border-0 resize-none focus:outline-none focus:ring-0"
      />

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 mx-4 w-full max-w-md bg-white rounded-lg">
            <h3 className="mb-4 text-lg font-semibold">Insert Link</h3>

            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  URL
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Link Text (optional)
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Link text"
                  className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowLinkDialog(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md transition-colors hover:bg-gray-200">
                Cancel
              </button>
              <button
                onClick={insertLink}
                className="px-4 py-2 text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700">
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Info */}
      {isFocused && selection.start !== selection.end && (
        <div className="px-4 py-2 text-xs text-blue-700 bg-blue-50 border-t border-blue-200">
          Selected: {selection.end - selection.start} characters
        </div>
      )}
    </div>
  );
};

export default RichTextToolbar;
